import { Link } from 'react-router-dom';
import { blogPosts } from '../../../mocks/blogPosts';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function Blog() {
  const featuredPost = blogPosts[0];
  const recentPosts = blogPosts.slice(1, 4);

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: featuredRef, isVisible: featuredVisible } = useScrollAnimation();
  const { ref: gridRef, visibleItems } = useStaggerAnimation(recentPosts.length);
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <section className="py-16 sm:py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ease-out ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-petrol-100 px-4 py-2 rounded-full mb-4 sm:mb-6">
            <i className="ri-article-line text-petrol-600 text-base sm:text-lg"></i>
            <span className="text-petrol-700 font-bold text-xs sm:text-sm uppercase tracking-wide">Blog</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-petrol-900 mb-4 sm:mb-6 px-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Conteúdo Sobre{' '}
            <span className="text-strategic-green-500">Gestão de Riscos</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-petrol-700 max-w-3xl mx-auto leading-relaxed px-4">
            Artigos técnicos e estratégicos sobre seguros, gestão de riscos e economia inteligente.
          </p>
        </div>

        {/* Featured Post */}
        <div
          ref={featuredRef as React.RefObject<HTMLDivElement>}
          className={`mb-8 sm:mb-12 transition-all duration-700 ease-out ${featuredVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Link
            to={`/blog/${featuredPost.slug}`}
            className="block bg-gradient-to-br from-petrol-900 to-petrol-700 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-300 group cursor-pointer"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="relative h-64 sm:h-80 lg:h-auto bg-petrol-200" style={{ minHeight: '256px' }}>
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="480"
                />
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-petrol-900/80 to-transparent"></div>
              </div>
              <div className="p-6 sm:p-8 lg:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-action-orange-500/20 backdrop-blur-sm border border-action-orange-400/30 px-3 py-1.5 rounded-full mb-4 sm:mb-6 w-fit">
                  <i className="ri-star-fill text-action-orange-400 text-sm"></i>
                  <span className="text-action-orange-300 font-bold text-xs uppercase tracking-wide">Artigo em Destaque</span>
                </div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 group-hover:text-strategic-green-400 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {featuredPost.title}
                </h3>
                <p className="text-sm sm:text-base lg:text-lg text-gray-200 leading-relaxed mb-4 sm:mb-6">{featuredPost.excerpt}</p>
                <div className="flex items-center justify-between gap-4 text-xs sm:text-sm text-gray-300">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{featuredPost.date}</span>
                    <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>{featuredPost.readTime}</span>
                  </div>
                  <span className="flex items-center gap-2 text-strategic-green-400 font-semibold group-hover:gap-3 transition-all">
                    Ler artigo <i className="ri-arrow-right-line text-base sm:text-lg"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Recent Posts Grid */}
        <div ref={gridRef as React.RefObject<HTMLDivElement>} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-12">
          {recentPosts.map((post, index) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className={`bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl border border-petrol-100 hover:border-strategic-green-400 group cursor-pointer transition-all duration-700 ease-out ${visibleItems[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              <div className="relative h-48 sm:h-56 overflow-hidden bg-petrol-100">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="480"
                />
                <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
                  <span className="bg-strategic-green-500 text-white px-3 py-1.5 rounded-full text-xs font-bold">{post.category}</span>
                </div>
              </div>
              <div className="p-5 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-petrol-900 mb-2 sm:mb-3 group-hover:text-strategic-green-600 transition-colors line-clamp-2">{post.title}</h3>
                <p className="text-sm sm:text-base text-petrol-700 leading-relaxed mb-4 line-clamp-2">{post.excerpt}</p>
                <div className="flex items-center justify-between gap-4 text-xs sm:text-sm text-petrol-600">
                  <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{post.date}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>{post.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`text-center transition-all duration-700 ease-out ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <a
            href="/blog"
            className="inline-flex items-center justify-center px-8 py-4 bg-action-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-action-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            Ver Todos os Artigos
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>

      </div>
    </section>
  );
}
